import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { resendUserAccessLink, deleteUserAccount } from "@/lib/userAdmin.functions";
import { setClientTierOverride } from "@/lib/tier.functions";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/admin/clients")({
  head: () => ({
    meta: [
      { title: "Manage Clients — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Manage Clients" nav={ADMIN_NAV}>
        <ClientsTable />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface ClientRow {
  id: string;
  user_id: string;
  full_name: string;
  household_size: number | null;
  onboarding_step: number;
  onboarding_status: string;
  assigned_agent_id: string | null;
  assigned_agent_name: string;
  active_policies: number;
  total_coverage: number;
  last_activity: string | null;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function ClientsTable() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [agents, setAgents] = useState<Array<{ id: string; name: string }>>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reassignTo, setReassignTo] = useState<string>("");
  const [drawer, setDrawer] = useState<ClientRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClientRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const resendFn = useServerFn(resendUserAccessLink);
  const deleteFn = useServerFn(deleteUserAccount);
  const setTierFn = useServerFn(setClientTierOverride);
  const [tierValue, setTierValue] = useState<string>("none");
  const [tierNotes, setTierNotes] = useState("");
  const [savingTier, setSavingTier] = useState(false);
  const [drawerDetail, setDrawerDetail] = useState<{
    policies: Array<{ id: string; policy_number: string | null; carrier: string | null; face_amount: number | null; status: string }>;
    documents: Array<{ id: string; name: string; created_at: string }>;
    appointments: Array<{ id: string; scheduled_at: string; status: string }>;
  } | null>(null);

  useEffect(() => {
    if (!drawer) { setDrawerDetail(null); return; }
    let cancelled = false;
    (async () => {
      const [pol, docs, appts, tierRow] = await Promise.all([
        supabase.from("policies").select("id, policy_number, carrier, face_amount, status").eq("client_id", drawer.id).order("effective_date", { ascending: false }),
        supabase.from("documents").select("id, name, created_at").eq("client_id", drawer.id).order("created_at", { ascending: false }).limit(10),
        supabase.from("appointments").select("id, scheduled_at, status").eq("client_id", drawer.id).order("scheduled_at", { ascending: false }).limit(5),
        supabase.from("clients").select("tier_override, tier_qualifier_notes").eq("id", drawer.id).maybeSingle(),
      ]);
      if (cancelled) return;
      setTierValue((tierRow.data as any)?.tier_override ?? "none");
      setTierNotes((tierRow.data as any)?.tier_qualifier_notes ?? "");
      setDrawerDetail({
        policies: (pol.data ?? []) as any,
        documents: (docs.data ?? []) as any,
        appointments: (appts.data ?? []) as any,
      });
    })();
    return () => { cancelled = true; };
  }, [drawer]);

  useEffect(() => { void load(); }, []);

  async function saveTier() {
    if (!drawer) return;
    const override = tierValue === "none" ? null : (tierValue as "library" | "family" | "legacy");
    if (override && !tierNotes.trim()) {
      toast.error("A written reason is required when granting a tier override.");
      return;
    }
    setSavingTier(true);
    try {
      await setTierFn({ data: { clientId: drawer.id, tierOverride: override, notes: tierNotes.trim() } });
      toast.success(override ? "Tier override saved." : "Tier override cleared.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save tier override.");
    } finally {
      setSavingTier(false);
    }
  }

  async function load() {
    const [clientsRes, profilesRes, polRes] = await Promise.all([
      supabase.from("clients").select("id, user_id, household_size, onboarding_step, onboarding_status, assigned_agent_id, updated_at"),
      supabase.from("profiles").select("id, full_name"),
      supabase.from("policies").select("client_id, face_amount, status"),
    ]);

    const profileMap = new Map<string, string>();
    for (const p of (profilesRes.data ?? []) as Array<{ id: string; full_name: string | null }>) {
      profileMap.set(p.id, p.full_name || "Unnamed");
    }

    const policyMap = new Map<string, { count: number; total: number }>();
    for (const p of (polRes.data ?? []) as Array<{ client_id: string; face_amount: number | null; status: string }>) {
      const isActive = p.status === "active";
      const entry = policyMap.get(p.client_id) ?? { count: 0, total: 0 };
      if (isActive) entry.count += 1;
      entry.total += Number(p.face_amount) || 0;
      policyMap.set(p.client_id, entry);
    }

    const merged: ClientRow[] = ((clientsRes.data ?? []) as Array<{
      id: string; user_id: string; household_size: number | null; onboarding_step: number;
      onboarding_status: string; assigned_agent_id: string | null; updated_at: string | null;
    }>).map((c) => ({
      ...c,
      full_name: profileMap.get(c.user_id) ?? "—",
      assigned_agent_name: c.assigned_agent_id ? (profileMap.get(c.assigned_agent_id) ?? "—") : "Unassigned",
      active_policies: policyMap.get(c.id)?.count ?? 0,
      total_coverage: policyMap.get(c.id)?.total ?? 0,
      last_activity: c.updated_at,
    }));
    setRows(merged);

    const agentsRes = await supabase.from("agents").select("user_id");
    const agentList = ((agentsRes.data ?? []) as Array<{ user_id: string }>).map((a) => ({
      id: a.user_id, name: profileMap.get(a.user_id) ?? "Strategist",
    }));
    setAgents(agentList);
  }

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (statusFilter !== "all" && r.onboarding_status !== statusFilter) return false;
        if (agentFilter !== "all" && r.assigned_agent_id !== agentFilter) return false;
        if (search && !r.full_name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [rows, statusFilter, agentFilter, search],
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function bulkReassign() {
    if (!reassignTo || selected.size === 0) return;
    const ids = Array.from(selected);
    const { error } = await supabase.from("clients").update({ assigned_agent_id: reassignTo }).in("id", ids);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id, action: "admin.clients_reassigned", target_type: "client",
      metadata: { count: ids.length, agent_id: reassignTo },
    });
    toast.success(`${ids.length} client(s) reassigned`);
    setSelected(new Set());
    setReassignTo("");
    void load();
  }

  async function resend(row: ClientRow) {
    try {
      await resendFn({ data: { userId: row.user_id } });
      toast.success(`Access link sent to ${row.full_name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resend link.");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteFn({ data: { userId: deleteTarget.user_id, kind: "client" } });
      toast.success(`${deleteTarget.full_name} removed`);
      setDeleteTarget(null);
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete account.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "32px", letterSpacing: "-0.01em" }}>
        Manage <em className="text-bronze-600 italic font-normal">Clients</em>
      </h2>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All onboarding</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
          </SelectContent>
        </Select>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-48 bg-white border-bronze-600/30"><SelectValue placeholder="Strategist" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All strategists</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…" className="w-64 bg-white border-bronze-600/30"
        />

        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="font-sans text-[12px] text-charcoal-500">{selected.size} selected</span>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger className="w-48 bg-white border-bronze-600/30"><SelectValue placeholder="Reassign to…" /></SelectTrigger>
              <SelectContent>
                {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <PrimaryButton onClick={() => void bulkReassign()}>Apply</PrimaryButton>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
        <table className="w-full">
          <thead className="bg-cream-50 border-b border-bronze-600/20">
            <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              <th className="px-4 py-3 w-10"></th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Onboarding</th>
              <th className="px-4 py-3 text-right">Policies</th>
              <th className="px-4 py-3 text-right">Coverage</th>
              <th className="px-4 py-3">Strategist</th>
              <th className="px-4 py-3">Last Activity</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bronze-600/15">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center font-sans text-[13px] text-charcoal-500">No clients found.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-cream-50/60 cursor-pointer" onClick={() => setDrawer(r)}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} className="border-bronze-600" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-navy-800 text-cream-50 font-sans font-semibold text-[11px] flex items-center justify-center">
                      {r.full_name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-sans text-[13px] text-charcoal-900">{r.full_name}</p>
                      <p className="font-sans text-[11px] text-charcoal-500">Household of {r.household_size ?? 1}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 w-40">
                  <Progress value={(r.onboarding_step / 5) * 100} className="h-1.5 bg-bronze-600/20" />
                  <p className="mt-1 font-sans text-[10px] text-charcoal-500">{Math.round((r.onboarding_step / 5) * 100)}%</p>
                </td>
                <td className="px-4 py-3 text-right font-display text-[14px] text-navy-800">{r.active_policies}</td>
                <td className="px-4 py-3 text-right font-display text-[14px] text-navy-800">{fmt(r.total_coverage)}</td>
                <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">{r.assigned_agent_name}</td>
                <td className="px-4 py-3 font-sans text-[11px] text-charcoal-500">
                  {r.last_activity ? new Date(r.last_activity).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="text-charcoal-500 hover:text-navy-800">
                      <MoreVertical size={16} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => void resend(r)}>
                        Resend access link
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-danger" onClick={() => setDeleteTarget(r)}>
                        Delete account
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the client's account, profile, and login. Policies and
              documents tied to the household will lose their owner. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="bg-cream-50">
          <SheetHeader>
            <SheetTitle className="font-display text-navy-800 text-[24px]">{drawer?.full_name}</SheetTitle>
          </SheetHeader>
          {drawer && (
            <div className="mt-6 space-y-5">
              <div>
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Strategist</p>
                <p className="mt-1 font-sans text-[14px] text-charcoal-900">{drawer.assigned_agent_name}</p>
              </div>
              <div className="border-t border-bronze-600/20 pt-4">
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Membership tier override</p>
                <p className="mt-1 font-sans text-[11px] text-charcoal-500">
                  Legacy Member is granted by a person, not by a formula. A written reason is required.
                </p>
                <Select value={tierValue} onValueChange={setTierValue}>
                  <SelectTrigger className="mt-3 border-bronze-600/30 bg-white font-sans text-[13px]">
                    <SelectValue placeholder="No override" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No override (automatic)</SelectItem>
                    <SelectItem value="library">Library Member</SelectItem>
                    <SelectItem value="family">Family Member</SelectItem>
                    <SelectItem value="legacy">Legacy Member</SelectItem>
                  </SelectContent>
                </Select>
                {tierValue !== "none" && (
                  <Textarea
                    value={tierNotes}
                    onChange={(e) => setTierNotes(e.target.value)}
                    placeholder="Why does this family qualify? Required — this is recorded."
                    className="mt-3 min-h-[84px] border-bronze-600/30 bg-white font-sans text-[13px]"
                  />
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <PrimaryButton
                    disabled={savingTier || (tierValue !== "none" && !tierNotes.trim())}
                    onClick={() => void saveTier()}
                  >
                    {savingTier ? "Saving…" : "Save tier"}
                  </PrimaryButton>
                  {tierValue !== "none" && !tierNotes.trim() && (
                    <p className="self-center font-sans text-[11px] text-danger">A written reason is required.</p>
                  )}
                </div>
              </div>
              <div>
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Coverage</p>
                <p className="mt-1 font-display text-navy-800 text-[28px]">{fmt(drawer.total_coverage)}</p>
                <p className="font-sans text-[12px] text-charcoal-500">{drawer.active_policies} active policies</p>
              </div>
              <div>
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Onboarding</p>
                <Progress value={(drawer.onboarding_step / 5) * 100} className="mt-2 h-2 bg-bronze-600/20" />
                <p className="mt-1 font-sans text-[12px] text-charcoal-500">Step {drawer.onboarding_step} of 5 · {drawer.onboarding_status}</p>
              </div>
              <div className="border-t border-bronze-600/20 pt-4">
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Policies</p>
                <ul className="mt-2 divide-y divide-bronze-600/10">
                  {(drawerDetail?.policies ?? []).length === 0 && (
                    <li className="py-2 font-sans text-[12px] text-charcoal-500">No policies on file.</li>
                  )}
                  {(drawerDetail?.policies ?? []).map((p) => (
                    <li key={p.id} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-sans text-[12.5px] text-charcoal-900 truncate">{p.policy_number ?? "—"} · {p.carrier ?? "—"}</p>
                        <p className="font-sans text-[11px] text-charcoal-500 capitalize">{p.status}</p>
                      </div>
                      <span className="font-display text-[13px] text-navy-800">{fmt(Number(p.face_amount) || 0)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-bronze-600/20 pt-4">
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Recent documents</p>
                <ul className="mt-2 divide-y divide-bronze-600/10">
                  {(drawerDetail?.documents ?? []).length === 0 && (
                    <li className="py-2 font-sans text-[12px] text-charcoal-500">No documents.</li>
                  )}
                  {(drawerDetail?.documents ?? []).map((d) => (
                    <li key={d.id} className="py-2 flex items-center justify-between gap-2">
                      <span className="font-sans text-[12.5px] text-charcoal-900 truncate">{d.name}</span>
                      <span className="font-sans text-[11px] text-charcoal-500">{new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-bronze-600/20 pt-4">
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Upcoming appointments</p>
                <ul className="mt-2 divide-y divide-bronze-600/10">
                  {(drawerDetail?.appointments ?? []).length === 0 && (
                    <li className="py-2 font-sans text-[12px] text-charcoal-500">None scheduled.</li>
                  )}
                  {(drawerDetail?.appointments ?? []).map((a) => (
                    <li key={a.id} className="py-2 flex items-center justify-between gap-2">
                      <span className="font-sans text-[12.5px] text-charcoal-900">{new Date(a.scheduled_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      <span className="font-sans text-[11px] text-charcoal-500 capitalize">{a.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}